import { faStore } from '@fortawesome/pro-solid-svg-icons/faStore';
import { faStoreSlash } from '@fortawesome/pro-solid-svg-icons/faStoreSlash';
import type { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportError, useMutation } from '@furality/frontend-stack';
import { Button, ToastEnum } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '@/components/FontAwesomeIcon.tsx';
import TooltipHelper from '@/components/TooltipHelper.tsx';
import useReload from '@/lib/hooks/useReload.ts';
import useToasts from '@/lib/useToast.ts';

type DealerShareToggleButtonProps = {
  /**
   * Options callback that gets called after visibility toggle completes successfully.
   */
  ownerDisplayLocation?: boolean;
  dealerId: number;
} & ComponentProps<typeof Button>;

const DealerShareToggleButton: FC<DealerShareToggleButtonProps> = ({
  ownerDisplayLocation,
  dealerId,
  ...props
}) => {
  const toasts = useToasts();
  const { t } = useTranslation(['common', 'dealers']);

  const reloadDealer = useReload({
    queryKey: ['dealer', dealerId],
  });

  const setLocation = useMutation({
    mutationFn: async (id: number) => {
      if (ownerDisplayLocation) {
        const response = await fox.value.dealerDisableOwnerLocation({
          params: {
            boothId: id,
          },
        });
        switch (response.status) {
          case 204:
            break;
          default:
            throw new ReportError(response);
        }
      } else {
        const response = await fox.value.dealerEnableOwnerLocation({
          params: {
            boothId: id,
          },
        });
        switch (response.status) {
          case 204:
            break;
          default:
            throw new ReportError(response);
        }
      }

      toasts.pushToast(
        ToastEnum.Info,
        ownerDisplayLocation
          ? t('dealers:dealer.location.hide.toast')
          : t('dealers:dealer.location.show.toast'),
      );
      await reloadDealer();
    },
  });

  return (
    <TooltipHelper
      id="share-dd-location"
      title={
        ownerDisplayLocation
          ? t('dealers:dealer.location.hide.tooltip')
          : t('dealers:dealer.location.show.tooltip')
      }
      child={
        <Button
          variant={ownerDisplayLocation ? 'dark' : 'outline-warning'}
          rounded
          size="sm"
          icon={<FontAwesomeIcon icon={ownerDisplayLocation ? faStore : faStoreSlash} />}
          onClick={() => setLocation.mutate(dealerId)}
          {...props}
        >
          {ownerDisplayLocation
            ? t('dealers:dealer.location.sharing_on')
            : t('dealers:dealer.location.sharing_off')}
        </Button>
      }
    />
  );
};

export default DealerShareToggleButton;
